<template lang="pug">
div(:class="isMobileScreen ? 'pt-4 px-4 pb-4 mobile-screen' : 'pt-6 px-8 pb-8'")
  //- Header section
  .d-flex.align-center(:class="isMobileScreen ? 'justify-end' : 'justify-space-between'")
    .yz-heading-5.neutral700--text(v-if="!isMobileScreen") Packages
    hq-branch-dropdown(v-model="branchFilter" :class="isMobileScreen ? '' : 'px-5'")

  //- Content card
  yz-card.mt-4(:class="isMobileScreen ? 'px-3 py-3' : 'px-5 py-4'")
    //- Desktop: Tabs and actions row
    template(v-if="!isMobileScreen")
      .d-flex.justify-space-between.align-center
        yz-tabs(v-model="activeTab" type="line")
          yz-tab(label="All")
          yz-tab(label="Active")
          yz-tab(label="Out of stock")
          yz-tab(label="Archived")
        .d-flex.align-center.gap-2
          yz-search-btn(v-model="searchText" size="lg" @search="onSearch")
          customize-column-menu(type="hq_packages" :show-tooltip="false" icon-size="21")
          yz-btn(label="Add package" size="lg" @click="onAddPackage")

    //- Mobile: Scrollable tabs and compact actions
    template(v-else)
      .d-flex.align-center.justify-space-between.mb-2
        yz-search-btn(v-model="searchText" size="lg" @search="onSearch")
        yz-btn(icon-only icon="$yzi-plus" size="lg" @click="onAddPackage")
      .overflow-x-auto
        .d-flex.align-center(style="width: max-content;")
          div(v-for="tab in tabItems" :key="tab.id")
            .px-3.py-2.cursor-pointer(@click="activeTab = tab.id")
              span.yz-medium-body-sm(:class="activeTab === tab.id ? 'neutral700--text' : 'neutral500--text'") {{ tab.label }}
            .bar-active(v-if="activeTab === tab.id")

    yz-divider.my-0

    //- Filter row
    packages-filter-row(v-if="isInitialized" ref="filterRow" :filters.sync="filters" @reset="onResetFilters" @update:filters="onFiltersChange")

    //- Table
    packages-table(v-if="isInitialized" ref="packagesTable" :is-mobile="isMobileScreen" :active-tab="activeTab" :search-text="searchText" :filters="filters" :location-filter="branchFilterIds" @edit-item="onEditItem" @delete-item="onDeleteItem")

  //- Delete package dialog
  delete-confirmation-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :loading="isDeleting" @cancel="onDeleteDialogClosed" @delete="onConfirmDelete")
    .text-lg.font-medium Are you sure you want to delete this package?
    .text-md.mt-4 This action will be done immediately and can't be undone. Click "Confirm" to proceed
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CustomizeColumnMenu from '@/components/connect/CustomizeColumnMenu.vue'
import PackagesFilterRow from './PackagesFilterRow.vue'
import PackagesTable from './PackagesTable.vue'
import HqBranchDropdown from '@/components/hq/inventory/HqBranchDropdown.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'

export default {
  components: { CustomizeColumnMenu, PackagesFilterRow, PackagesTable, HqBranchDropdown, DeleteConfirmationDialog },
  data () {
    return {
      activeTab: 0,
      searchText: '',
      filters: [],
      branchFilter: null,
      isInitialized: false,
      showDeleteDialog: false,
      isDeleting: false,
      selectedPackage: null,
      tabNameToIndex: {
        all: 0,
        active: 1,
        out_of_stock: 2,
        archived: 3
      },
      tabIndexToName: ['all', 'active', 'out_of_stock', 'archived']
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
    branchFilterIds () {
      return this.branchFilter ? this.branchFilter.map(b => b.id) : null
    },
    tabItems () {
      return [
        { id: 0, label: 'All' },
        { id: 1, label: 'Active' },
        { id: 2, label: 'Out of stock' },
        { id: 3, label: 'Archived' }
      ]
    }
  },
  watch: {
    activeTab (newTab) {
      this.updateTabQuery(newTab)
    },
    '$route.query.tab': {
      handler (newTabName) {
        if (newTabName) {
          const tabIndex = this.tabNameToIndex[newTabName]
          if (tabIndex !== undefined && tabIndex !== this.activeTab) {
            this.activeTab = tabIndex
          }
        }
      }
    },
    '$route.query.search': {
      handler (newSearch) {
        const searchValue = newSearch || ''
        if (this.searchText !== searchValue) {
          this.searchText = searchValue
        }
      }
    },
    '$route.query.filters': {
      handler (newFilters) {
        const decoded = this.deserializeFilters(newFilters)
        if (JSON.stringify(this.filters) !== JSON.stringify(decoded)) {
          this.filters = decoded
        }
      }
    }
  },
  created () {
    this.initializeStateFromRoute()
    this.isInitialized = true
  },
  methods: {
    ...mapActions(['showMessage']),
    initializeStateFromRoute () {
      const { tab, search, filters } = this.$route.query

      if (tab && this.tabNameToIndex[tab] !== undefined) {
        this.activeTab = this.tabNameToIndex[tab]
      } else {
        this.updateTabQuery(0)
      }

      if (search) {
        this.searchText = search
      }

      if (filters) {
        this.filters = this.deserializeFilters(filters)
      }
    },
    updateTabQuery (tabIndex) {
      const tabName = this.tabIndexToName[tabIndex]
      if (tabName && this.$route.query.tab !== tabName) {
        this.$router.replace({
          query: { ...this.$route.query, tab: tabName }
        }).catch(() => {})
      }
    },
    updateSearchQuery (search) {
      const newQuery = { ...this.$route.query }
      if (search) {
        newQuery.search = search
      } else {
        delete newQuery.search
      }
      if (this.$route.query.search !== newQuery.search) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    updateFiltersQuery (filters) {
      const newQuery = { ...this.$route.query }
      const serialized = this.serializeFilters(filters)
      if (serialized) {
        newQuery.filters = serialized
      } else {
        delete newQuery.filters
      }
      if (this.$route.query.filters !== newQuery.filters) {
        this.$router.replace({ query: newQuery }).catch(() => {})
      }
    },
    serializeFilters (filters) {
      if (!filters || filters.length === 0) {
        return null
      }
      try {
        const json = JSON.stringify(filters)
        return btoa(encodeURIComponent(json))
      } catch (error) {
        console.error('Error serializing filters:', error)
        return null
      }
    },
    deserializeFilters (base64String) {
      if (!base64String) {
        return []
      }
      try {
        const json = decodeURIComponent(atob(base64String))
        return JSON.parse(json)
      } catch (error) {
        console.error('Error deserializing filters:', error)
        return []
      }
    },
    onSearch (value) {
      this.searchText = value
      this.updateSearchQuery(value)
      this.$refs.packagesTable.currentPage = 1
      this.$refs.packagesTable.fetchPackages()
    },
    onResetFilters () {
      this.filters = []
      this.updateFiltersQuery([])
    },
    onFiltersChange (newFilters) {
      this.filters = newFilters
      this.updateFiltersQuery(newFilters)
    },
    onAddPackage () {
      this.$router.push({ name: 'HQNewPackage' })
    },
    onEditItem (item) {
      this.$router.push({ name: 'HQEditPackage', params: { id: item.id } })
    },
    onDeleteItem (item) {
      this.selectedPackage = item
      this.showDeleteDialog = true
    },
    onConfirmDelete () {
      if (!this.selectedPackage) return
      this.isDeleting = true
      this.$axios.delete(`/clinics/${this.company.slug}/hq/packages/${this.selectedPackage.id}`)
        .then(() => {
          this.showMessage(['success', 'Package deleted successfully'])
          this.selectedPackage = null
          this.showDeleteDialog = false
          this.$refs.packagesTable.fetchPackages()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isDeleting = false
        })
    },
    onDeleteDialogClosed () {
      this.selectedPackage = null
    }
  }
}
</script>

<style lang="scss" scoped>
.overflow-x-auto {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar { display: none; }
  scrollbar-width: none;
}
.bar-active {
  height: 2px;
  width: 100%;
  margin: auto;
  margin-top: -2px;
  background-color: #333;
}
</style>
